import { CMS_ACCESS_ENROLLMENT_FLAG } from "@/constants/AccessProgram";
import { captureError } from "@/lib/monitoring";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import type { EncounterProgram } from "@curai/js-fhir-tools";

export const VISIT_CLOSURE_STEP_IDS = {
  billingCodes: "billingCodes",
  diagnosisCharting: "diagnosisCharting",
  dxBillingCodes: "dxBillingCodes",
  programEnrollment: "programEnrollment",
  visitTime: "visitTime",
  cptCodes: "cptCodes",
  billingSummary: "billingSummary",
  carePlanPreview: "carePlanPreview",
  rejectAsyncVisit: "rejectAsyncVisit",
} as const;

export type VisitClosureStepId =
  (typeof VISIT_CLOSURE_STEP_IDS)[keyof typeof VISIT_CLOSURE_STEP_IDS];

export const VISIT_CLOSURE_STEP_NAMES: Record<VisitClosureStepId, string> = {
  [VISIT_CLOSURE_STEP_IDS.billingCodes]: "Billing Codes",
  [VISIT_CLOSURE_STEP_IDS.diagnosisCharting]: "Medical Record Review",
  [VISIT_CLOSURE_STEP_IDS.dxBillingCodes]: "Diagnoses & Billing Codes",
  [VISIT_CLOSURE_STEP_IDS.programEnrollment]: "ACCESS Enrollment",
  [VISIT_CLOSURE_STEP_IDS.visitTime]: "Visit Time",
  [VISIT_CLOSURE_STEP_IDS.cptCodes]: "CPT Codes",
  [VISIT_CLOSURE_STEP_IDS.billingSummary]: "Billing Summary",
  [VISIT_CLOSURE_STEP_IDS.carePlanPreview]: "Care Plan Preview",
  [VISIT_CLOSURE_STEP_IDS.rejectAsyncVisit]: "Reject Async Visit",
};

const LEGACY_STEP_IDS = [
  VISIT_CLOSURE_STEP_IDS.billingCodes,
  VISIT_CLOSURE_STEP_IDS.visitTime,
  VISIT_CLOSURE_STEP_IDS.cptCodes,
  VISIT_CLOSURE_STEP_IDS.billingSummary,
] as const;

const SINGLE_ENCOUNTER_STEP_IDS = [
  VISIT_CLOSURE_STEP_IDS.dxBillingCodes,
  VISIT_CLOSURE_STEP_IDS.visitTime,
  VISIT_CLOSURE_STEP_IDS.cptCodes,
  VISIT_CLOSURE_STEP_IDS.billingSummary,
] as const;

export const REJECT_ASYNC_VISIT_STEP_IDS = [
  VISIT_CLOSURE_STEP_IDS.rejectAsyncVisit,
] as const;

export const BILLINGLESS_STEP_IDS = [
  VISIT_CLOSURE_STEP_IDS.carePlanPreview,
] as const;

/** Prepend visit diagnosis charting to a step flow. */
export const withVisitDxCharting = (
  steps: readonly VisitClosureStepId[],
): VisitClosureStepId[] => [VISIT_CLOSURE_STEP_IDS.diagnosisCharting, ...steps];

/** Insert `programEnrollment` after whichever code-collection step is present. */
export const withProgramEnrollment = (
  steps: readonly VisitClosureStepId[],
): VisitClosureStepId[] => {
  const anchor = steps.findIndex(
    (s) =>
      s === VISIT_CLOSURE_STEP_IDS.dxBillingCodes ||
      s === VISIT_CLOSURE_STEP_IDS.billingCodes,
  );
  if (anchor < 0) {
    captureError(
      new Error(
        "withProgramEnrollment: no billingCodes/dxBillingCodes anchor found",
      ),
      { steps: steps.join(",") },
    );

    const billingSummaryIndex = steps.indexOf(
      VISIT_CLOSURE_STEP_IDS.billingSummary,
    );
    if (billingSummaryIndex < 0) {
      return [...steps, VISIT_CLOSURE_STEP_IDS.programEnrollment];
    }

    return [
      ...steps.slice(0, billingSummaryIndex),
      VISIT_CLOSURE_STEP_IDS.programEnrollment,
      ...steps.slice(billingSummaryIndex),
    ];
  }

  return [
    ...steps.slice(0, anchor + 1),
    VISIT_CLOSURE_STEP_IDS.programEnrollment,
    ...steps.slice(anchor + 1),
  ];
};

export const needsVisitDiagnosisCharting = (visitDiagnosesCount: number) =>
  visitDiagnosesCount > 0;

// A single-encounter visit has no child encounters and either has visit
// diagnoses or is enabled by the feature flag.
export const isSingleEncounterFlow = ({
  visitDiagnosesCount,
  hasChildEncounters,
  isSingleEncounterVisitsEnabled,
}: {
  visitDiagnosesCount: number;
  hasChildEncounters: boolean;
  isSingleEncounterVisitsEnabled: boolean;
}) =>
  !hasChildEncounters &&
  (needsVisitDiagnosisCharting(visitDiagnosesCount) ||
    isSingleEncounterVisitsEnabled);

export const useVisitClosureSteps = ({
  visitDiagnosesCount,
  hasChildEncounters,
  encounterProgram,
  visitCompleted,
  mode = "billing",
}: {
  visitDiagnosesCount: number;
  hasChildEncounters: boolean;
  encounterProgram: EncounterProgram | null;
  visitCompleted: boolean;
  mode?: "billing" | "billingless" | "rejectAsyncVisit";
}) => {
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );
  const { data: accessFlag } = useConfigCatFeatureFlag(
    CMS_ACCESS_ENROLLMENT_FLAG,
  );
  const requiresEnrollmentDecision =
    accessFlag === true && encounterProgram !== null && visitCompleted;
  const hasVisitDiagnosisCharting =
    needsVisitDiagnosisCharting(visitDiagnosesCount);
  const isSingleEncounter = isSingleEncounterFlow({
    visitDiagnosesCount,
    hasChildEncounters,
    isSingleEncounterVisitsEnabled: isSingleEncounterVisitsEnabled === true,
  });
  const requiresDiagnosisBillingStep = isSingleEncounter;
  const baseSteps = isSingleEncounter
    ? SINGLE_ENCOUNTER_STEP_IDS
    : LEGACY_STEP_IDS;
  const stepsWithVisitDxCharting =
    isSingleEncounter && hasVisitDiagnosisCharting
      ? withVisitDxCharting(baseSteps)
      : baseSteps;
  const billingSteps = requiresEnrollmentDecision
    ? withProgramEnrollment(stepsWithVisitDxCharting)
    : stepsWithVisitDxCharting;
  const rejectAsyncVisitSteps = hasVisitDiagnosisCharting
    ? withVisitDxCharting(REJECT_ASYNC_VISIT_STEP_IDS)
    : REJECT_ASYNC_VISIT_STEP_IDS;
  const billinglessSteps = hasVisitDiagnosisCharting
    ? withVisitDxCharting(BILLINGLESS_STEP_IDS)
    : BILLINGLESS_STEP_IDS;
  const steps =
    mode === "rejectAsyncVisit"
      ? rejectAsyncVisitSteps
      : mode === "billingless"
        ? billinglessSteps
        : billingSteps;

  return {
    requiresDiagnosisBillingStep,
    requiresEnrollmentDecision,
    steps,
    stepNames: steps.map((step) => VISIT_CLOSURE_STEP_NAMES[step]),
  };
};
